import { useContext, useEffect } from "react";

import { buildFulfillmentCheckout, OfferedFulfillmentMethod } from "@simplyk/common";
import { Control, useWatch } from "react-hook-form";

import { useFrontendAuctionContext } from "../../../../common/contexts/FrontendAuctionContext";
import { FrontendFormContext } from "../../../../common/contexts/FrontendFormContext";
import { getAuctionBidAmounts, getOrderAmountTotal, getOrderTaxAmount } from "../../../../common/helpers/rates";

import { TicketingPaymentInput } from "@/common/types/ticketing";
import { DiscountWithRateMappings, TicketingFrontendPageOutput } from "@/common/types/trpc";

export const useSyncDisplayedTotalAmount = ({
  control,
  ticketing,
  validDiscount,
  isAuction,
}: {
  control: Control<TicketingPaymentInput>;
  ticketing: TicketingFrontendPageOutput;
  validDiscount: DiscountWithRateMappings | null;
  isAuction: boolean;
}): void => {
  const { commandBidsRateIdsMap, highestBidsMapByRateId, activeAutoBidMaxByRateId } = useFrontendAuctionContext();
  const { setDisplayedFormAmount, setDisplayedFormTaxAmount } = useContext(FrontendFormContext);
  const ticketsPurchased = useWatch({ control, name: "ticketsPurchased" });
  const fulfillmentMethod = useWatch({ control, name: "fulfillmentMethod" });
  const extraDonation = useWatch({ control, name: "extraDonation" });

  const { shippingFeeAmount } = buildFulfillmentCheckout({
    formCategory: ticketing.formCategory,
    offeredMethods: (ticketing.offeredFulfillmentMethods ?? []) as OfferedFulfillmentMethod[],
    selectedMethod: fulfillmentMethod ?? null,
    ticketingShippingFeeAmount: ticketing.shippingFeeAmount ?? 0,
  });

  const orderTaxAmount = getOrderTaxAmount({
    ticketsPurchased,
    rates: ticketing.rates || [],
    isAuction,
    discount: validDiscount,
    discountRateMappings: validDiscount?.selectedRateIds || [],
    auctionBids: isAuction
      ? getAuctionBidAmounts({
          ticketsPurchased,
          commandBidsRateIdsMap,
          highestBidsMapByRateId,
          activeAutoBidMaxByRateId,
          useAutoBidCeiling: true,
        })
      : undefined,
  });

  const orderAmount =
    getOrderAmountTotal({
      ticketsPurchased,
      rates: ticketing.rates || [],
      extraDonation: extraDonation || 0,
      isAuction,
      discount: validDiscount,
      commandBidsRateIdsMap,
      highestBidsMapByRateId,
      activeAutoBidMaxByRateId,
      useAutoBidCeiling: true,
      discountRateMappings: validDiscount?.selectedRateIds || [],
    }) +
    (isAuction ? 0 : shippingFeeAmount) +
    orderTaxAmount;

  useEffect(() => {
    setDisplayedFormAmount(orderAmount);
    setDisplayedFormTaxAmount(orderTaxAmount);
  }, [orderAmount, orderTaxAmount, setDisplayedFormAmount, setDisplayedFormTaxAmount]);
};
